import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Confetti from "react-confetti";
import "./LandingPage.css";
import "./SearchResults.css";
import people from "./brownpeople";

interface Person {
  firstName: string;
  lastName: string;
  title: string;
  slug: string;
  dei: boolean;
  category: string;
}

type CategoryTag =
  | "academic"
  | "finance"
  | "student life"
  | "legal"
  | "human resources"
  | "communications"
  | "admissions"
  | "technology"
  | "miscellaneous";

type TypeFilter = "all" | "DEI";

const PAGE_SIZE = 25;

const ChatSection = styled.div<{ isDragging?: boolean }>`
  width: 100%;
  height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e0e0e0;
  background-color: ${({ isDragging }) => (isDragging ? "#e0f7ff" : "#f7f9fc")};
  border: ${({ isDragging }) => (isDragging ? "2px dashed #007bff" : "none")};
  transition: background-color 0.2s, border 0.2s;
  padding-bottom: 80px;
`;

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
`;

const FundingContainer = styled.div`
  text-align: center;
  margin: 40px 0 0;
`;
const FundingAmount = styled.div`
  font-size: 45px;
  font-weight: bold;
  color: #e74c3c;
`;
const FundingLabel = styled.div`
  font-size: 18px;
  color: #ccc;
  text-transform: uppercase;
  margin-top: 8px;
`;
const TellButton = styled.button`
  background-color: #fff;
  color: #1f1f1f;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin: 20px 0;
  transition: background-color 0.2s;
  &:hover {
    background-color: #aaa;
  }
`;

const FilterBar = styled.div`
  position: sticky;
  top: 50px;
  margin-top: 10px;
  z-index: 50;
  background: #1f1f1f;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5rem 1rem;
  padding: 1rem 0;
`;

const CategoryDropdown = styled.select`
  background: #222;
  color: #eee;
  border: 1px solid #555;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  outline: none;
`;

const AltLanding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showSuccess = searchParams.get("success") === "true";

  const [animatedAmount, setAnimatedAmount] = useState(0);
  useEffect(() => {
    const target = -510_000_000;
    const duration = 1000;
    const start = performance.now();
    const step = (now: number) => {
      const prog = Math.min((now - start) / duration, 1);
      setAnimatedAmount(Math.floor(prog * target));
      if (prog < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  const [selectedType, setSelectedType] = useState<TypeFilter>("all");
  const [selectedCategory, setSelectedCategory] = useState<CategoryTag | "">(
    ""
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [randomPerson, setRandomPerson] = useState<Person | null>(null);
  const [page, setPage] = useState(1);

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedType(e.target.value as TypeFilter);
    setRandomPerson(null);
    setPage(1);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value as CategoryTag);
    setRandomPerson(null);
    setPage(1);
  };

  const handleRandom = (e: React.MouseEvent) => {
    e.preventDefault();
    setRandomPerson(people[Math.floor(Math.random() * people.length)]);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const resize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const filtered = people.filter((p) => {
    if (selectedType === "DEI" && !p.dei) return false;
    if (
      selectedCategory &&
      p.category.toLowerCase() !== selectedCategory.toLowerCase()
    )
      return false;
    if (searchTerm) {
      const hay = `${p.firstName} ${p.lastName} ${p.title}`.toLowerCase();
      if (!hay.includes(searchTerm.toLowerCase())) return false;
    }
    return true;
  });

  const displayList = randomPerson
    ? [randomPerson]
    : filtered.slice(0, page * PAGE_SIZE);

  return (
    <div>
      <div className="landing-page">
        <Helmet>
          <title>Bloat@Brown</title>
          <meta
            name="description"
            content="Meet Brown's sprawling bureaucracy funded by $90,000+ tuition and fees — and until recently, American taxpayers."
          />
        </Helmet>
        {showSuccess && <Confetti />}
        <FundingContainer>
          <FundingAmount>
            {animatedAmount <= 0
              ? `-$${Math.abs(animatedAmount).toLocaleString()}`
              : `$${animatedAmount.toLocaleString()}`}
          </FundingAmount>
          <FundingLabel>Federal funds lost by Brown</FundingLabel>
        </FundingContainer>
        <TellButton
          onClick={() =>
            navigate("/chat", { state: { query: "brown university" } })
          }
        >
          Email Brown yourself
        </TellButton>
        <h2 className="center-subtitle">
          Meet Brown's sprawling bureaucracy funded by $90,000+ tuition and fees
          — and until recently, American taxpayers.
        </h2>
      </div>
      <FilterBar>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "1rem",
            width: "100%",
          }}
        >
          <CategoryDropdown value={selectedType} onChange={handleTypeChange}>
            <option value="all">All Types</option>
            <option value="DEI">DEI</option>
          </CategoryDropdown>
          <CategoryDropdown
            value={selectedCategory}
            onChange={handleCategoryChange}
          >
            <option value="">All Departments</option>
            <option value="academic">Academic</option>
            <option value="finance">Finance</option>
            <option value="student life">Student Life</option>
            <option value="legal">Legal</option>
            <option value="human resources">Human Resources</option>
            <option value="communications">Communications</option>
            <option value="admissions">Admissions</option>
            <option value="technology">Technology</option>
            <option value="miscellaneous">Miscellaneous</option>
          </CategoryDropdown>
          <input
            type="text"
            placeholder="Search name or title..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setRandomPerson(null);
              setPage(1);
            }}
            style={{
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #555",
              fontSize: "0.9rem",
            }}
          />
        </div>
        <div style={{ width: "100%", textAlign: "center" }}>
          <a
            href="#"
            onClick={handleRandom}
            style={{
              cursor: "pointer",
              color: "#ccc",
              fontSize: "14px",
              textDecoration: "underline",
            }}
          >
            View random employee
          </a>
        </div>
      </FilterBar>
      <div className="search-results-list">
        {displayList.map((person, idx) => (
          <Link
            key={idx}
            to={`/brown/${person.slug}`}
            className="search-result-box"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <div className="profile-name">
              {person.firstName} {person.lastName}
            </div>
            <div className="profile-title">{person.title}</div>
            <div className="staff-badge">{person.category}</div>
          </Link>
        ))}
      </div>
      {!randomPerson && filtered.length > page * PAGE_SIZE && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button
            onClick={() => setPage((prev) => prev + 1)}
            style={{
              background: "#222",
              color: "#fff",
              border: "1px solid #555",
              borderRadius: "999px",
              padding: "0.5rem 1.5rem",
              cursor: "pointer",
              fontSize: "0.9rem",
              marginBottom: "1rem",
            }}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default AltLanding;
